import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, ArrowLeft, RefreshCw, X } from 'lucide-react';
import { navigate } from '../utils/navigation';

interface OTPVerificationProps {
  phone: string;
  onClose: () => void;
  onBack: () => void;
  onSuccess?: () => void;
  redirectAfterLogin?: string;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({ 
  phone, 
  onClose, 
  onBack, 
  onSuccess,
  redirectAfterLogin
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes
  const [isResending, setIsResending] = useState(false);
  const { loginWithOTP, resendOTP, loading, error, clearError } = useAuth();

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // Clear errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      if (/^\d$/.test(pastedData[i])) {
        newOtp[i] = pastedData[i];
      }
    }
    
    setOtp(newOtp);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      return;
    }

    try {
      console.log('OTP verification attempt for phone:', phone);
      await loginWithOTP(phone, otpString);
      console.log('OTP verification successful, redirectAfterLogin:', redirectAfterLogin);

      // Import necessary functions for navigation
      const { goToAdmin, goToCommunity } = await import('../utils/navigation');
      const { useAuth } = await import('../contexts/AuthContext');
      
      // Get current auth context
      const { isAdmin, isCommittee, isVendor } = useAuth();
      
      // Determine redirect target based on user role
      let target = redirectAfterLogin;
      if (!target) {
        if (isAdmin) {
          target = '/admin';
          goToAdmin();
        } else if (isCommittee || isVendor) {
          target = '/community';
          goToCommunity();
        } else {
          target = '/';
          navigate(target);
        }
      } else {
        navigate(target);
      }
      
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('OTP verification error:', err);
      // Error is handled by auth context
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    try {
      await resendOTP(phone);
      setTimeLeft(120);
      setOtp(['', '', '', '', '', '']);
    } catch (err) {
      // Error is handled by auth context
    } finally {
      setIsResending(false);
    }
  };

  const otpString = otp.join('');
  const isValid = otpString.length === 6;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6 md:p-8 relative max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Back Button */}
        <button
          onClick={onBack}
          className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>

        <div className="text-center mb-6 sm:mb-8 mt-2 sm:mt-4">
          <div className="flex justify-center mb-4">
                        <img 
              src="/logo.png" 
              alt="Durga Puja Network" 
              className="h-20 w-20 sm:h-24 sm:w-24 object-contain"
              onError={(e) => e.currentTarget.style.display = 'none'}
            />
          </div>
          <div className="w-16 h-16 bg-dpn-pink bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-dpn-pink" />
          </div>
          <h2 className="font-runtime text-2xl sm:text-3xl font-bold text-black mb-2">
            Verify Your Phone
          </h2>
          <p className="font-lucy-rose text-gray-600">
            We've sent a 6-digit code to
          </p>
          <p className="font-lucy-rose text-dpn-pink font-medium">
            {phone}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* OTP Input Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4 text-center font-lucy-rose">
              Enter verification code
            </label>
            <div className="flex justify-center space-x-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-12 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none"
                />
              ))}
            </div>
          </div>

          {/* Timer and Resend */}
          <div className="text-center">
            {timeLeft > 0 ? (
              <p className="font-lucy-rose text-sm text-gray-600">
                Resend code in{' '}
                <span className="font-medium text-dpn-pink">
                  {formatTime(timeLeft)}
                </span>
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isResending || loading}
                className="font-lucy-rose text-sm text-dpn-pink hover:text-dpn-raspberry font-medium transition-colors disabled:opacity-50 flex items-center justify-center mx-auto"
              >
                {(isResending || loading) ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                ) : null}
                {(isResending || loading) ? 'Sending...' : 'Resend Code'}
              </button>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm font-lucy-rose text-center">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!isValid || loading}
            className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            ) : null}
            {loading ? 'Verifying...' : 'Verify & Continue'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="font-lucy-rose text-xs text-gray-500">
            Didn't receive the code? Check your WhatsApp or try again.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;